import { createSignal, createEffect, onMount, onCleanup, Show, createMemo, untrack } from "solid-js";
import Map from "../../../../components/charts/Map";
import LoadingOverlay from "../../../../components/utilities/Loading";
import { unifiedDataStore } from "../../../../store/unifiedDataStore";
import { selectedTime, setSelectedTime, requestTimeControl, releaseTimeControl, timeWindow, setTimeWindow, liveMode, registerFleetMapInstance } from "../../../../store/playbackStore";
import { sidebarState } from "../../../../store/globalStore";
import { persistantStore } from "../../../../store/persistantStore";
const { selectedClassName, selectedProjectId, selectedDate } = persistantStore;
import { debug } from "../../../../utils/console";
import { waitForPaint } from "../../../../utils/waitForRender";
import * as d3 from "d3";
import { setCurrentDataset, getCurrentDatasetTimezone } from "../../../../store/datasetTimezoneStore";
import { getData, getTimezoneForDate } from "../../../../utils/global";
import { apiEndpoints } from "@config/env";
import { setRaceOptions, setIsTrainingHourMode, selectedSources as filterStoreSelectedSources, raceOptions, selectedRacesTimeseries, isTrainingHourMode } from "../../../../store/filterStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { logPageLoad } from "../../../../utils/logging";
import { resolveFleetInitialTimeWindow, fleetTimeWindowWhenRaceFilterApplied, fleetRaceFilterSignature, classifyFleetDateRacesResponse, FLEET_PLAYBACK_START_OFFSET_MS, type FleetRaceModeStatus } from "../../../../utils/fleetTimeWindow";
import { useDashboardShareRestore } from "../../../../hooks/useDashboardShareRestore";
import {
  FLEET_MAP_SHARE_PAGE_KEY,
  applyMapShareState,
  buildFleetMapShareState,
} from "../../../../utils/mapShareState";

interface FleetMapProps {
  objectName?: string;
  [key: string]: any;
}

/** Selected boat source IDs derived from filterStore so selection is shared with FleetTimeSeries and FleetVideo. */
function selectedBoatIdsFromFilterStore(): Set<number> {
  const names = filterStoreSelectedSources();
  if (!sourcesStore.isReady() || !Array.isArray(names) || names.length === 0) return new Set();
  const sources = sourcesStore.sources();
  const ids = new Set<number>();
  for (const name of names) {
    const n = String(name).trim().toLowerCase();
    const s = sources.find((x) => String(x.source_name).trim().toLowerCase() === n);
    if (s && Number.isFinite(Number(s.source_id))) ids.add(Number(s.source_id));
  }
  return ids;
}

export default function MapComponent(props: FleetMapProps) {
  const [isLoading, setIsLoading] = createSignal<boolean>(true);
  /** Selected boat source IDs from filterStore (shared with FleetTimeSeries and FleetVideo) — which sources are *enabled* in timeseries settings. */
  const selectedBoatIds = createMemo(selectedBoatIdsFromFilterStore);
  /** Boat(s) selected on the map for highlight (thick 3px track). Distinct from "enabled" — only these get thick line. */
  const [highlightedBoatIds, setHighlightedBoatIds] = createSignal<Set<number>>(new Set());
  /** Track if we've checked for data availability (declared early so createEffect below can use it) */
  const [hasCheckedData, setHasCheckedData] = createSignal(false);
  const [hasNoData, setHasNoData] = createSignal(false);
  /** Map click: toggle highlight (3px). Click to add/remove boat from highlight; multiple boats can be highlighted. Do NOT change enable/disable — that is done in TimeSeries settings. */
  const onToggleBoatSelection = (sourceId: number) => {
    const current = highlightedBoatIds();
    if (current.has(sourceId)) {
      const next = new Set(current);
      next.delete(sourceId);
      setHighlightedBoatIds(next);
    } else {
      setHighlightedBoatIds(new Set([...current, sourceId]));
    }
  };
  let hasInitializedTime = false;
  let checkInterval: ReturnType<typeof setInterval> | null = null;
  let lastDate: string | null = null;
  let lastFleetRaceFilterSig = "";
  const SESSION_STORAGE_KEY = 'fleetmap_timeWindow';
  const [raceModeStatus, setRaceModeStatus] = createSignal<FleetRaceModeStatus>("pending");
  let restoredInitialTimeWindow: number | null = null;
  let skipNextFleetRaceFilterDefault = false;

  useDashboardShareRestore(
    FLEET_MAP_SHARE_PAGE_KEY,
    () => buildFleetMapShareState(highlightedBoatIds()),
    (payload) => {
      if (typeof payload.timeWindow === "number" && Number.isFinite(payload.timeWindow) && payload.timeWindow >= 0) {
        restoredInitialTimeWindow = payload.timeWindow;
        skipNextFleetRaceFilterDefault = true;
      }
      return applyMapShareState(payload, FLEET_MAP_SHARE_PAGE_KEY, {
        sessionStorageKey: SESSION_STORAGE_KEY,
        setHighlightedSourceIds: setHighlightedBoatIds,
      });
    }
  );

  // Check if timeline is drawn (has SVG with x-axis)
  const isTimelineDrawn = () => {
    try {
      const chartContainer = document.querySelector('.map-container .chart-container');
      if (!chartContainer) return false;
      
      const svg = d3.select(chartContainer).select('svg');
      if (svg.empty()) return false;
      
      const xAxisGroup = svg.select('.x-axis');
      if (xAxisGroup.empty()) return false;
      
      // Check if axis has ticks (indicating it's fully drawn)
      const ticks = xAxisGroup.selectAll('.tick');
      return !ticks.empty();
    } catch (error: unknown) {
      return false;
    }
  };

  // Get minimum time from data
  const getMinTimeFromData = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = selectedDate();
    
    if (!className || !projectId || !date) {
      return null;
    }

      try {
        const ymd = String(date).replace(/[-/]/g, '');
        const selectedSourceIds = selectedBoatIdsFromFilterStore();
        if (selectedSourceIds.size > 0) {
          debug('FleetMap: Using selected sources for min time calculation', { count: selectedSourceIds.size, ids: Array.from(selectedSourceIds) });
        }
        const mapData = await unifiedDataStore.fetchMapDataForDay(className, Number(projectId), ymd, selectedSourceIds.size > 0 ? selectedSourceIds : undefined);
      
      if (!mapData || mapData.length === 0) {
        return null;
      }

      let minTime = Infinity;
      for (const point of mapData) {
        const timestamp = point.Datetime || point.timestamp || point.time || point.datetime;
        if (timestamp) {
          const time = new Date(timestamp).getTime();
          if (!isNaN(time) && time < minTime) {
            minTime = time;
          }
        }
      }

      if (Number.isFinite(minTime)) {
        return minTime;
      }
    } catch (error: unknown) {
      debug('FleetMap: Error fetching min time from data', error as any);
    }
    
    return null;
  };

  // Function to initialize selectedTime after timeline is drawn
  const initializeTimeFromTimeline = async () => {
    if (hasInitializedTime) return;

    // First check if timeline is drawn
    if (!isTimelineDrawn()) {
      return; // Timeline not ready yet
    }

    // Get minimum time from data
    const minTime = await getMinTimeFromData();
    if (minTime === null) {
      return; // Data not ready yet
    }

    const training = isTrainingHourMode();
    const selected = selectedRacesTimeseries();
    const savedTimeWindow = sessionStorage.getItem(SESSION_STORAGE_KEY);
    // The user's saved window wins over the race-context default (resolveFleetInitialTimeWindow only
    // substitutes when the saved value is unusable here) — selecting a race no longer forces Full.
    const initialTimeWindow =
      restoredInitialTimeWindow != null
        ? restoredInitialTimeWindow
        : resolveFleetInitialTimeWindow({
            trainingHourMode: training,
            raceOptions: raceOptions(),
            selectedRaces: selected,
            savedTimeWindow,
          });
    setTimeWindow(initialTimeWindow);
    debug('FleetMap: Initialized timeWindow', {
      value: initialTimeWindow,
      fromStorage: !!savedTimeWindow,
      trainingHourMode: isTrainingHourMode(),
      hasRealRace: selectedRacesTimeseries().length > 0,
    });

    // Timeline is drawn and data is available - wait for simple paint before hiding loading
    // Use simple paint detection to avoid blocking UI
    waitForPaint(2).then(() => {
      debug('FleetMap: Paint complete, hiding loading overlay');
      setIsLoading(false);
    }).catch((error) => {
      debug('FleetMap: Error waiting for paint, hiding loading anyway:', error);
      setIsLoading(false);
    });

    // Check if selectedTime is at default or very close to minimum
    const currentTime = selectedTime();
    const defaultTime = new Date('1970-01-01T12:00:00Z');
    const isDefault = currentTime.getTime() === defaultTime.getTime();
    
    // Check if current time is at or very close to minimum (within 10 seconds)
    const timeDiff = Math.abs(currentTime.getTime() - minTime);
    const isAtMinTime = timeDiff < 10000;

    // Playback starts a fixed 2 minutes into the data, independent of the time window, so the
    // reference-delta trace is already live when the page settles (see FLEET_PLAYBACK_START_OFFSET_MS).
    const targetMs = minTime + FLEET_PLAYBACK_START_OFFSET_MS;

    // Only set if it's default or at minimum time
    if (isDefault || isAtMinTime) {
      const targetTime = new Date(targetMs);

      if (requestTimeControl('fleetmap')) {
        debug('FleetMap: Setting selectedTime to data start + 2 min', {
          minTime: new Date(minTime).toISOString(),
          currentTime: currentTime.toISOString(),
          targetTime: targetTime.toISOString(),
          timeWindow: initialTimeWindow,
          isDefault,
          isAtMinTime
        });
        setSelectedTime(targetTime, 'fleetmap');
        hasInitializedTime = true;
        setTimeout(() => {
          releaseTimeControl('fleetmap');
        }, 100);
      }
    } else {
      // Check if it's already at our target
      const targetDiff = Math.abs(currentTime.getTime() - targetMs);
      if (targetDiff < 10000) {
        hasInitializedTime = true;
      }
    }
  };

  // Set timezone when date changes (similar to MapTimeSeries)
  // For fleet mode, we use the timezone from the first dataset for that date
  // In practice, all datasets for the same date/project should have the same timezone
  createEffect(async () => {
    debug('🕐 FleetMap: Timezone effect triggered');
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = selectedDate();
    
    debug('🕐 FleetMap: Timezone effect values', {
      className,
      projectId,
      date,
      hasValues: !!(className && projectId && date)
    });
    
    if (!className || !projectId || !date) {
      debug('🕐 FleetMap: Missing values, clearing timezone');
      await setCurrentDataset(className || '', projectId || 0, null);
      return;
    }

    try {
      // Get timezone for this date using the dedicated endpoint
      const ymd = String(date).replace(/[-/]/g, '');
      const timezoneResponse = await getData(
        `${apiEndpoints.app.datasets}/date/timezone?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(ymd)}`
      );

      // The API returns { success: true, data: { timezone: "...", dataset_id: ... } } or { timezone: "...", dataset_id: ... }
      const timezoneData = timezoneResponse?.data || timezoneResponse || {};
      const timezone = timezoneData.timezone;
      const datasetId = timezoneData.dataset_id;
      
      if (timezone && datasetId) {
        await setCurrentDataset(className, projectId, datasetId);
        const tz = getCurrentDatasetTimezone();
        debug('🕐 FleetMap: Timezone set from date endpoint', {
          datasetId: datasetId,
          timezone: tz,
          date: date
        });
        debug('FleetMap: Set timezone from date endpoint (representative for fleet)', {
          datasetId: datasetId,
          timezone: tz
        });
      } else {
        // No timezone found for this date, clear timezone
        debug('🕐 FleetMap: No timezone found for date', { date });
        await setCurrentDataset(className, projectId, null);
      }
    } catch (error: unknown) {
      debug('FleetMap: Error setting timezone', error as any);
      await setCurrentDataset(className, projectId, null);
    }
  });

  // Don't load races here - MapContainer will load them when it mounts
  // We just check if they exist. This avoids conflicts and render cycles.

  // Default to Full (or 30min cap) when fleet race filter changes — not on manual time-window toggles.
  createEffect(() => {
    const training = isTrainingHourMode();
    const selected = selectedRacesTimeseries();
    const sig = fleetRaceFilterSignature(training, selected);
    if (sig === lastFleetRaceFilterSig) return;
    lastFleetRaceFilterSig = sig;
    if (skipNextFleetRaceFilterDefault) {
      skipNextFleetRaceFilterDefault = false;
      debug("FleetMap: preserving shared timeWindow for restored race filter", { selected, training, sig });
      return;
    }
    // untrack: this effect reacts to the race filter only — the current window is an input, not a trigger.
    const currentWindow = untrack(() => timeWindow());
    const nextWindow = fleetTimeWindowWhenRaceFilterApplied(training, selected, currentWindow);
    if (nextWindow !== currentWindow) {
      setTimeWindow(nextWindow);
    }
    debug("FleetMap: timeWindow after race filter", { value: nextWindow, selected, training, sig });
  });

  // Watch for date changes to reset initialization
  createEffect(() => {
    const currentDate = selectedDate();
    if (currentDate !== lastDate) {
      lastDate = currentDate;
      lastFleetRaceFilterSig = "";
      hasInitializedTime = false; // Reset when date changes
      setIsLoading(true); // Show loading when date changes
      setHasCheckedData(false); // Reset data check when date changes
      setHasNoData(false); // Reset no data state when date changes
      setRaceModeStatus("pending");
      debug('FleetMap: Date changed, resetting time initialization');
    }
  });

  // Poll for timeline to be ready and initialize time
  onMount(() => {
    const unregisterFleetMap = registerFleetMapInstance();
    void logPageLoad("FleetMap.tsx", "Fleet Map", "Loaded");
    let attempts = 0;
    const maxAttempts = 50; // Try for up to 5 seconds (50 * 100ms)
    
    checkInterval = setInterval(() => {
      attempts++;
      
      if (hasInitializedTime) {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        return;
      }
      
      if (attempts > maxAttempts) {
        debug('FleetMap: Max attempts reached waiting for timeline');
        setIsLoading(false); // Hide loading even if timeline didn't initialize
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        return;
      }

      // Try to initialize from timeline (async, but we don't await it)
      initializeTimeFromTimeline().catch((error: unknown) => {
        debug('FleetMap: Error in initializeTimeFromTimeline', error as any);
        setIsLoading(false); // Hide loading on error
      });
    }, 100); // Check every 100ms

    onCleanup(() => {
      unregisterFleetMap();
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    });
  });

  // Track whether we've fetched races for the current date and if any exist
  // (We fetch directly instead of relying on filterStore.raceOptions, which MapContainer
  // clears on mount before its async fetch completes, causing a false "Multi-Map Not Available".)
  const [hasCheckedRacesForDate, setHasCheckedRacesForDate] = createSignal(false);
  const [hasRacesForDate, setHasRacesForDate] = createSignal(false);

  createEffect(() => {
    const date = selectedDate();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!date || !className || !projectId) {
      setHasCheckedRacesForDate(false);
      setHasRacesForDate(false);
      setIsTrainingHourMode(false);
      setRaceModeStatus("pending");
      return;
    }

    setHasCheckedRacesForDate(false);
    setHasRacesForDate(false);
    setIsTrainingHourMode(false);
    setRaceModeStatus("pending");

    // Preload events for the day into agg.events so MapSettings sees races/legs (non-blocking)
    const dateStrNorm = String(date).length === 8 && !String(date).includes('-')
      ? `${String(date).slice(0, 4)}-${String(date).slice(4, 6)}-${String(date).slice(6, 8)}`
      : String(date);
    unifiedDataStore.preloadEventsForDate(className, Number(projectId), dateStrNorm).catch(() => {});

    let cancelled = false;
    const dateStr = dateStrNorm;
    getTimezoneForDate(className, Number(projectId), dateStr).then((timezone) => {
      let racesUrl = `${apiEndpoints.app.datasets}/date/races?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(dateStr)}&from_race_events=1`;
      if (timezone) racesUrl += `&timezone=${encodeURIComponent(timezone)}`;
      return getData(racesUrl);
    })
      .then((racesResp: { data?: unknown[]; [key: string]: unknown }) => {
        if (cancelled) return;
        const racesList = (racesResp && (racesResp.data || racesResp)) || [];
        const classified = classifyFleetDateRacesResponse(racesList);
        setRaceModeStatus(classified.status);
        setHasRacesForDate(classified.raceOptions.length > 0);
        setHasCheckedRacesForDate(true);
        setIsTrainingHourMode(classified.trainingHourMode);
        if (classified.raceOptions.length > 0) {
          setRaceOptions(classified.raceOptions);
        } else {
          setRaceOptions([]);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        debug('FleetMap: Error fetching races for date', error as any);
        setHasCheckedRacesForDate(true);
        setHasRacesForDate(false);
        setIsTrainingHourMode(false);
        setRaceModeStatus("error");
      });

    // onCleanup, not a returned function: Solid discards createEffect return values, so the
    // cancelled flag never flipped. Switching dates quickly let a stale date's response overwrite
    // raceOptions / isTrainingHourMode / raceModeStatus in the global filterStore after the newer
    // one had already landed. raceModeStatus is the authoritative signal for the no-race window, so
    // a stale write there is user-visible. In-flight responses are now dropped rather than applied
    // late; the effect always re-runs to completion for the new date.
    onCleanup(() => {
      cancelled = true;
    });
  });

  // Show "no races" only after we've checked and confirmed no races for this date (never on live map — uses the live feed, not day races)
  const shouldShowNoRaces = createMemo(() => {
    if (sidebarState() === 'live' || liveMode()) return false;
    return hasCheckedRacesForDate() && !hasRacesForDate();
  });

  const hasRaces = createMemo(() => hasRacesForDate());

  // Check for data availability after map has finished loading (re-run when selection, race options, or selected hour change)
  createEffect(async () => {
    const loading = isLoading();
    const date = selectedDate();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    selectedBoatIds(); // track filterStore selection so we re-check when user changes sources
    // Re-run when race options are set (e.g. date/races loaded for training hours) so we don't show "no data" before data is ready
    const options = raceOptions && raceOptions();
    // Re-run when selected hour/race changes so we re-check and clear stale no-data state
    selectedRacesTimeseries && selectedRacesTimeseries();

    // Only check when loading is complete and we have the necessary info
    if (loading || !date || !className || !projectId) {
      setHasCheckedData(false);
      setHasNoData(false);
      return;
    }

    // Reset no-data state when selection/options change so we don't show stale overlay while re-checking
    setHasNoData(false);

    // Wait a bit for the map to fully render and for date/races to set options before checking
    const timeout = setTimeout(async () => {
      try {
        const ymd = String(date).replace(/[-/]/g, '');
        const selectedSourceIds = selectedBoatIdsFromFilterStore();
        const mapData = await unifiedDataStore.fetchMapDataForDay(className, Number(projectId), ymd, selectedSourceIds.size > 0 ? selectedSourceIds : undefined);
        
        // Check if there's no data
        if (!mapData || mapData.length === 0) {
          setHasNoData(true);
        } else {
          setHasNoData(false);
        }
        setHasCheckedData(true);
      } catch (error: unknown) {
        debug('FleetMap: Error checking data availability', error as any);
        setHasCheckedData(true);
        setHasNoData(false); // Don't show "no data" on error
      }
    }, 1000); // Wait 1 second after loading completes

    // onCleanup, not a returned function: Solid discards createEffect return values. This effect
    // tracks seven signals (loading/date/class/project/boats/raceOptions/selectedRaces), so each
    // change used to leave another 1 s timer armed, every one of them firing a full
    // fetchMapDataForDay. Registering here is safe: the effect body is synchronous up to this point
    // (the awaits live inside the setTimeout callback), so we are still inside the owner.
    onCleanup(() => clearTimeout(timeout));
  });

  // Show "no data" only when options are ready, we've checked, and there's no data.
  // Never show in training-hour mode: we removed hour filtering so full-day data is shown; the "no data" path was for race filtering and can wrongly hide when data exists.
  const shouldShowNoData = createMemo(() => {
    if (isTrainingHourMode()) return false;
    const optionsReady = raceOptions && raceOptions().length > 0;
    return optionsReady && hasRaces() && !isLoading() && hasCheckedData() && hasNoData();
  });

  // Watch for timeWindow changes and save to session storage
  createEffect(() => {
    const currentTimeWindow = timeWindow();
    // Only save if we've initialized (to avoid saving during initialization)
    if (hasInitializedTime) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, currentTimeWindow.toString());
      debug('FleetMap: Saved timeWindow to session storage', { value: currentTimeWindow });
    }
  });

  return (
    <div style="position: relative; width: 100%; height: 100%;">
      <Show when={shouldShowNoRaces()}>
        <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; background: var(--color-bg-card);">
          <div style="text-align: center; padding: 2rem;">
            <h2 style="font-size: 1.5rem; margin-bottom: 1rem; color: var(--color-text-primary);">
              Multi-Map Not Available
            </h2>
            <p style="font-size: 1rem; color: var(--color-text-secondary);">
              Multi-Map requires race data to function. No races are available for the selected date.
            </p>
          </div>
        </div>
      </Show>
      <Show when={!shouldShowNoRaces()}>
        <Show when={isLoading()}>
          <LoadingOverlay message="Loading map data..." />
        </Show>
        <Show when={shouldShowNoData()}>
          <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.3); z-index: 1000; pointer-events: none;">
            <div style="text-align: center; padding: 2rem; background: var(--color-bg-card); border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); pointer-events: auto;">
              <h2 style="font-size: 1.5rem; margin-bottom: 1rem; color: var(--color-text-primary);">
                No Data Available
              </h2>
              <p style="font-size: 1rem; color: var(--color-text-secondary);">
                No data is available for the selected date and sources.
              </p>
            </div>
          </div>
        </Show>
        <Map objectName={props?.objectName} sourceMode={'multi'} selectedBoatIds={highlightedBoatIds()} onToggleBoatSelection={onToggleBoatSelection} />
      </Show>
    </div>
  );
}
